import { Stroke, Point } from '../types';

/**
 * Stroke utility functions for managing drawing strokes
 */

/**
 * Generate a unique stroke ID
 */
export function generateStrokeId(): string {
  return `stroke_${uuidv4()}`;
}

/**
 * Create a stroke key for storage
 */
export function createStrokeKey(testId: string, questionId: string): string {
  return `strokes_${testId}_${questionId}`;
}

/**
 * Create a new stroke object
 */
export function createStroke(
  tool: string,
  color: string,
  width: number
): Stroke {
  return {
    id: generateStrokeId(),
    tool: tool as any, // Tool type from types/index.ts
    color,
    width,
    points: [],
    timestamp: Date.now()
  };
}

/**
 * Add point to stroke
 */
export function addPointToStroke(
  stroke: Stroke,
  point: Point & { pressure?: number }
): Stroke {
  return {
    ...stroke,
    points: [...stroke.points, point]
  };
}

/**
 * Calculate stroke bounding box
 */
export function getStrokeBounds(stroke: Stroke): {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
  width: number;
  height: number;
} {
  if (stroke.points.length === 0) {
    return { minX: 0, minY: 0, maxX: 0, maxY: 0, width: 0, height: 0 };
  }

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  stroke.points.forEach(point => {
    minX = Math.min(minX, point.x);
    minY = Math.min(minY, point.y);
    maxX = Math.max(maxX, point.x);
    maxY = Math.max(maxY, point.y);
  });

  return {
    minX,
    minY,
    maxX,
    maxY,
    width: maxX - minX,
    height: maxY - minY
  };
}

/**
 * Check if point is near stroke
 */
export function isPointNearStroke(
  point: Point,
  stroke: Stroke,
  threshold: number = 10
): boolean {
  return stroke.points.some(strokePoint => {
    const distance = Math.sqrt(
      Math.pow(point.x - strokePoint.x, 2) +
      Math.pow(point.y - strokePoint.y, 2)
    );
    return distance <= threshold;
  });
}

/**
 * Simplify stroke points using Douglas-Peucker algorithm
 */
export function simplifyStroke(
  stroke: Stroke,
  tolerance: number = 2
): Stroke {
  const simplifiedPoints = douglasPeucker(stroke.points, tolerance);
  return {
    ...stroke,
    points: simplifiedPoints
  };
}

/**
 * Douglas-Peucker line simplification algorithm
 */
function douglasPeucker(
  points: Point[],
  tolerance: number
): Point[] {
  if (points.length <= 2) return points;

  let maxDistance = 0;
  let maxIndex = 0;

  // Find the point with maximum distance from line between first and last
  for (let i = 1; i < points.length - 1; i++) {
    const distance = perpendicularDistance(
      points[i],
      points[0],
      points[points.length - 1]
    );
    
    if (distance > maxDistance) {
      maxDistance = distance;
      maxIndex = i;
    }
  }

  // If max distance is greater than tolerance, recursively simplify
  if (maxDistance > tolerance) {
    const leftPart = douglasPeucker(
      points.slice(0, maxIndex + 1),
      tolerance
    );
    const rightPart = douglasPeucker(
      points.slice(maxIndex),
      tolerance
    );

    return [...leftPart.slice(0, -1), ...rightPart];
  }

  // Otherwise, return just the endpoints
  return [points[0], points[points.length - 1]];
}

/**
 * Calculate perpendicular distance from point to line
 */
function perpendicularDistance(
  point: Point,
  lineStart: Point,
  lineEnd: Point
): number {
  const dx = lineEnd.x - lineStart.x;
  const dy = lineEnd.y - lineStart.y;

  if (dx === 0 && dy === 0) {
    // Line start and end are the same
    return Math.sqrt(
      Math.pow(point.x - lineStart.x, 2) +
      Math.pow(point.y - lineStart.y, 2)
    );
  }

  const normalLength = Math.sqrt(dx * dx + dy * dy);
  return Math.abs(
    (point.x - lineStart.x) * dy - (point.y - lineStart.y) * dx
  ) / normalLength;
}

/**
 * Merge nearby strokes
 */
export function mergeStrokes(
  strokes: Stroke[],
  distanceThreshold: number = 20,
  timeThreshold: number = 500
): Stroke[] {
  if (strokes.length <= 1) return strokes;

  const merged: Stroke[] = [];
  let currentGroup: Stroke[] = [strokes[0]];

  for (let i = 1; i < strokes.length; i++) {
    const currentStroke = strokes[i];
    const lastInGroup = currentGroup[currentGroup.length - 1];

    // Check if strokes should be merged
    const timeDiff = Math.abs(currentStroke.timestamp - lastInGroup.timestamp);
    const lastPoint = lastInGroup.points[lastInGroup.points.length - 1];
    const firstPoint = currentStroke.points[0];

    if (lastPoint && firstPoint) {
      const distance = Math.sqrt(
        Math.pow(lastPoint.x - firstPoint.x, 2) +
        Math.pow(lastPoint.y - firstPoint.y, 2)
      );

      if (
        timeDiff <= timeThreshold &&
        distance <= distanceThreshold &&
        currentStroke.tool === lastInGroup.tool &&
        currentStroke.color === lastInGroup.color
      ) {
        currentGroup.push(currentStroke);
      } else {
        // Start new group
        merged.push(mergeStrokeGroup(currentGroup));
        currentGroup = [currentStroke];
      }
    }
  }

  // Don't forget the last group
  if (currentGroup.length > 0) {
    merged.push(mergeStrokeGroup(currentGroup));
  }

  return merged;
}

/**
 * Merge a group of strokes into one
 */
function mergeStrokeGroup(strokes: Stroke[]): Stroke {
  if (strokes.length === 1) return strokes[0];

  const firstStroke = strokes[0];
  const allPoints: Point[] = [];

  strokes.forEach(stroke => {
    allPoints.push(...stroke.points);
  });

  return {
    ...firstStroke,
    points: allPoints,
    timestamp: strokes[strokes.length - 1].timestamp
  };
}

/**
 * Install uuid if not already installed
 */
function uuidv4(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}